import { currency } from '../../utils/currency';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from 'antd';
import { Asset, ASSET_STATUS_LABELS, AssetStatus } from '../../types';
import { AssetService } from './asset.service';
import { assetDisposalReasons, isAssetDisposed } from './disposal';
import { useGlobalContext } from '../../store/GlobalContext';

const { Title, Text } = Typography;

/**
 * The items that have left the estate, and how each one left.
 *
 * They stay on file rather than being deleted: the court accounting has to
 * show what was in the estate and what became of it. They simply stop counting
 * toward the remaining value, which is what the summary reports beside the
 * gross.
 */

/** Money renderer that tolerates a missing value rather than throwing. */
const money = currency;

/**
 * One page is asked for rather than paging through: the estate holds tens of
 * assets, not thousands, and the disposal test runs on the client so a
 * server-side page would be a page of the wrong set.
 */
const FETCH_LIMIT = 500;

const DisposedAssetsPage: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const { selectedEstate } = useGlobalContext();
  const estateId = selectedEstate?.uuid || '';

  const fetchAssets = useCallback(async () => {
    if (!estateId) return;

    setLoading(true);
    setError('');
    try {
      const response = await AssetService.fetchAssets({
        estateId,
        page: 1,
        limit: FETCH_LIMIT,
      });
      setAssets(response.data ?? []);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to fetch assets.');
    } finally {
      setLoading(false);
    }
  }, [estateId]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const disposed = assets.filter(isAssetDisposed);
  const grossValue = assets.reduce((total, a) => total + (Number(a.marketValue) || 0), 0);
  const disposedValue = disposed.reduce((total, a) => total + (Number(a.marketValue) || 0), 0);

  const columns: TableColumnsType<Asset> = [
    { title: 'REF', dataIndex: 'ref', key: 'ref', width: 120, fixed: 'left' },
    { title: 'ASSET NAME', dataIndex: 'name', key: 'name' },
    { title: 'ASSET TYPE', dataIndex: 'category', key: 'category' },
    {
      title: 'WORTH WHEN IT LEFT',
      dataIndex: 'marketValue',
      key: 'marketValue',
      render: money,
    },
    {
      title: 'ESTIMATED VALUE',
      dataIndex: 'estimatedValue',
      key: 'estimatedValue',
      render: money,
    },
    {
      title: 'CASH RECEIVED',
      dataIndex: 'cashReceived',
      key: 'cashReceived',
      render: money,
    },
    {
      title: 'HOW IT LEFT',
      key: 'disposal',
      render: (_: unknown, record: Asset) => (
        <Space size={[0, 4]} wrap>
          {assetDisposalReasons(record).map((reason) => (
            <Tag key={reason} color={reason === 'Thrown out' ? 'default' : 'blue'}>
              {reason}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (status: AssetStatus) => ASSET_STATUS_LABELS[status] ?? status,
    },
    {
      title: 'LAST UPDATED',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string) => (date ? new Date(date).toLocaleDateString() : '-'),
    },
  ];

  return (
    <div className="record-page" style={{ padding: 24 }}>
      <Title level={3}>Distributed and Closed Assets</Title>
      <Text type="secondary">
        Items that have left the estate. They no longer count toward the remaining estate
        value, and stay here as a closed file for the court accounting.
      </Text>

      <Row gutter={16} style={{ marginTop: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Items that have left" value={loading || error ? 'Unavailable' : disposed.length} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Value that has left" value={loading || error ? 'Unavailable' : money(disposedValue)} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            {/* The gross is unchanged by a disposal: it is the estate as it
                stood, which is the figure the court was shown. */}
            <Statistic title="Gross estate (unchanged)" value={loading || error ? 'Unavailable' : money(grossValue)} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginTop: '24px' }}>
        {error && (
          <Alert message={error} action={<Button onClick={() => fetchAssets()}>Retry</Button>} type="error" showIcon style={{ margin: '16px 0' }} />
        )}
        <p className="table-scroll-hint">Scroll within the table for more columns.</p>
        {!error && <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={loading ? [] : disposed}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true }}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: loading ? 'Loading assets...' : 'Nothing has left the estate yet.' }}
          />
        </Spin>}
      </Card>
    </div>
  );
};

export default DisposedAssetsPage;
